import { useState, useEffect } from "react";
import cn from "classnames";
import { tagular } from "@cohesion/tagular";

import data from "@constants/best-in-travel/2025";
import { BIT_PROP_TYPES } from "@constants/best-in-travel/2025/proptypes";

import { keyify } from "@utils/keyify";
import { getThemeClasses } from "@utils/bestInTravel/2025/getThemeClasses";
import { useBestInTravel } from "@context/bestInTravel/2025/bestInTravelContext";

// Sort  destinations by their category (country, region or city)
const destinationsByCategory = data.destinations.reduce((acc, destination) => {
  const index = acc.findIndex(obj => obj.name === destination.meta.category);

  if (index > -1) {
    acc[index].destinations.push(destination);
    return acc;
  }

  acc.push({
    name: destination.meta.category,
    destinations: [destination],
  });
  return acc;
}, []);

export function TOC({ category }) {
  // Find the index of the category if it exists
  const defaultCategoryIndex = category
    ? destinationsByCategory.findIndex(group => group.name === category.name)
    : 0;
  const [open, setOpen] = useState(defaultCategoryIndex);
  const { setDestinationsMenuOpen, setTheme } = useBestInTravel();

  // Set the theme based on the default category when the component mounts
  useEffect(() => {
    if (category) {
      setTheme(category.name);
    }
  }, [category, setTheme]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex w-auto gap-2 mb-4 sm:gap-4 sm:flex-wrap">
        {/* CATEGORIES NAVIGATION */}
        {destinationsByCategory.map((group, i) => {
          const themeClasses = getThemeClasses(group.name);

          return (
            <button
              key={group.name}
              type="button"
              onClick={() => {
                setOpen(i);
                setTheme(group.name);
                tagular("click", {
                  actionOutcome: "OPEN",
                  outboundUrl: "null",
                  webElement: {
                    location: "BIT_Navigation",
                    elementType: "BUTTON",
                    text: group.name,
                    position: i,
                  },
                });
              }}
              className={cn(
                "capitalize w-auto sm:w-32 rounded-pill px-6 py-2 font-semibold text-sm sm:text-md",
                open === i
                  ? `${themeClasses.backgroundCategoryBtn} text-white capitalize inline-block `
                  : "bg-transparent text-blue border-black-300 border"
              )}
            >
              {group.name}
            </button>
          );
        })}
      </div>

      {/* DESTINATIONS LIST */}
      {destinationsByCategory.map((group, i) => {
        const themeClasses = getThemeClasses(group.name);
        return (
          <div
            className={cn("flex flex-col gap-2 ", {
              hidden: open !== i,
            })}
            key={`items-${group.name}`}
          >
            {group.destinations.map(destination => {
              const getLink = () =>
                `/best-in-travel/${destination?.meta.category}#${keyify(destination.name)}`;

              return (
                <div className="last:pb-20" key={keyify(destination.name)}>
                  <a
                    className="block mb-4 no-underline sm:flex-wrap sm:w-auto"
                    href={getLink()}
                    onClick={() => {
                      setDestinationsMenuOpen(false);
                      tagular("click", {
                        actionOutcome: "INTERNALLINK",
                        outboundUrl: getLink(),
                        webElement: {
                          location: "BIT_Navigation",
                          elementType: "LINK",
                          name: group.name,
                          text: destination.name,
                          position: i,
                        },
                      });
                    }}
                  >
                    <div
                      className={cn(
                        "font-rama text-4xl sm:text-5xl font-extrabold uppercase hover:text-blue leading-none",
                        themeClasses.textSubMenuItems
                      )}
                    >
                      {destination.name}
                    </div>
                  </a>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

TOC.propTypes = {
  category: BIT_PROP_TYPES.category,
};
